import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DeviceNetwork, ProvisioningTemplate, ProvisioningTemplateCreate, ProvisioningTemplateDuplicate,
  ProvisioningTemplateUpdate, TemplatePreview,
} from '@arcnet/contracts';
import { api } from './client.js';
import { extKeys } from './hooks-extension.js';

/**
 * Provisioning templates (docs/29) and the phones that use them.
 *
 * One set of hooks for both doors: an account's templates, with the platform's beside them
 * (tenantId set), or the platform's own (tenantId null, /inventory/templates).
 */

const base = (tenantId: string | null): string =>
  tenantId ? `/v1/tenants/${tenantId}/provisioning/templates` : '/v1/provisioning/templates';

export const templateKeys = {
  all: ['provisioning-templates'] as const,
  list: (tenantId: string | null) => ['provisioning-templates', tenantId ?? 'platform'] as const,
};

export const useTemplates = (tenantId: string | null) =>
  useQuery({
    queryKey: templateKeys.list(tenantId),
    queryFn: () => api.get<{ data: ProvisioningTemplate[] }>(base(tenantId)),
  });

export function useCreateTemplate(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisioningTemplateCreate) => api.post<ProvisioningTemplate>(base(tenantId), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

/**
 * The whole template, replaced. Returned so the save stays pending until the list is read
 * back, and the editor never shows the version it just replaced.
 */
export function useSaveTemplate(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ProvisioningTemplateUpdate) =>
      api.put<ProvisioningTemplate>(`${base(tenantId)}/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useDeleteTemplate(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${base(tenantId)}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export interface DuplicatedTemplate {
  template: ProvisioningTemplate;
  /** Positions of the line keys the copy could not have, left out. */
  dropped: number[];
  /** How many of the phone's own keys the copy's model does not have. */
  dropped_fixed?: number;
}

/** What a draft would write for its keys, and which line keys it empties (docs/29 decision 14). */
export interface TemplatePreviewResult extends TemplatePreview {
  cleared: number[];
}

/**
 * The settings beside the drawn phone, for a model and keys as JSON: the editor passes the
 * string once it has settled, so a query is asked once per pause, not per keystroke.
 */
export const useTemplatePreview = (tenantId: string | null, modelId: string, keysJson: string) =>
  useQuery({
    queryKey: ['provisioning-template-preview', tenantId ?? 'platform', modelId, keysJson] as const,
    queryFn: () => api.post<TemplatePreviewResult>(`${base(tenantId)}/preview`, {
      handset_model_id: modelId, keys: JSON.parse(keysJson) as unknown,
    }),
    enabled: modelId !== '',
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });

export function useDuplicateTemplate(tenantId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ProvisioningTemplateDuplicate) =>
      api.post<DuplicatedTemplate>(`${base(tenantId)}/${id}/duplicate`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

/** What adding a phone returns: its link and its own SIP credential, each shown once. */
export interface AddedPhone {
  id: string;
  provision_token: string;
  provisioning_url: string;
  provisioning_open_minutes: number;
  sip_credential: { username: string; realm: string; password: string };
}

/** A desk phone for an extension, by its MAC and a template. */
export function useAddPhone(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ extensionId, ...body }: {
      extensionId: string;
      mac_address: string;
      template_id: string;
      label: string | null;
      extra_lines?: Array<{ extension_id: string; line_number: number; label?: string }>;
      /** Only a static address is the phone's own; the template decides the rest. */
      network?: DeviceNetwork;
    }) => api.post<AddedPhone>(`/v1/tenants/${tenantId}/extensions/${extensionId}/phones`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: extKeys.devices(tenantId) }),
      qc.invalidateQueries({ queryKey: templateKeys.all }),
    ]),
  });
}

/** A phone given to another extension on the same account, keeping its identity. */
export function useMovePhone(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, extensionId }: { id: string; extensionId: string }) =>
      api.post<{ id: string; moved: boolean; personal_template_id: string | null }>(
        `/v1/tenants/${tenantId}/devices/${id}/move`, { extension_id: extensionId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: extKeys.devices(tenantId) }),
  });
}

/** One template for many phones of its model. */
export function useAssignTemplate(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { device_ids: string[]; template_id: string }) =>
      api.post<{ updated: number; skipped: Array<{ id: string; reason: string }> }>(
        `/v1/tenants/${tenantId}/devices/assign-template`, body),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: extKeys.devices(tenantId) }),
      qc.invalidateQueries({ queryKey: templateKeys.all }),
    ]),
  });
}
